import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Models
import { IValidateError, ValidateErrorCodes } from 'app/store/DMSBatches/DMSBatches.models';
// Store
import { useAppDispatch } from 'app/hooks/useStore';
// Actions
import { DMSUploadsActions } from 'app/store/DMSUploads/DMSUploads.slice';
// Mui
import { Theme, Box, FormHelperText, Tooltip } from '@mui/material';
// Icons
import {
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
// Components
import { Button, IconButton, Input } from 'app/components/Mui';
// Hooks
import useToggle from 'app/hooks/useToggle';
import useMenu from 'app/hooks/useMenu';
// 
import useUploadContext, { IFileItem, IFileItemsErrorGroup } from 'app/context/UploadFiles/useUploadContext';
import FileItemText from './FileItemText';
import FileItemCollectionSelector from './FileItemCollectionSelector';
import FileItemCommentDialog from './FileItemCommentDialog';

type Props = {
  group: IFileItemsErrorGroup;
  fileItem: IFileItem;
}

const FileItem:FC<Props> = ({
  // Props
  group, fileItem
}) => {
  const { t } = useTranslation();

  // Dispathc
  const dispatch = useAppDispatch();

  const { onRemove, onGroupRemove, onUpdate, features: { allowFileCollection, allowFileComment } } = useUploadContext();

  const { open, toggle } = useToggle();
  const { Menu, MenuItem, openMenu } = useMenu();

  const [ fileNameValue, setFileNameValue ] = useState(fileItem.name);
  const [ isEditing, setIsEditing ] = useState(false);

  const handleClose = (comment:string) => {
    if ( open ) toggle();
    onUpdate(fileItem.id, { comment: comment || '' });
  }

  const handlePreviewClick = () => {
    if ( !fileItem.file ) return;
    const url = URL.createObjectURL(fileItem.file);
    window.open(url, '_blank');
  }

  const handleRemoveClick = () => {
    onRemove(fileItem.id);
  }

  const handleGroupRemoveClick = () => {
    if ( !group || !group.fileItems ) return;
    onGroupRemove(group.fileItems, fileItem.id);
  }

  const handleRenameClick = () => setIsEditing(true);
  const handleCancelRenameClick = () => {
    setFileNameValue(fileItem.name)
    setIsEditing(false);
  }
  const handleSaveRenameClick = () => {
    onUpdate(fileItem.id, { name: fileNameValue }, true)
    setIsEditing(false);
    // Removing presigned url to upload file with new name once more
    dispatch(DMSUploadsActions.removePreSignedUrl(fileItem.id));
  }

  const renderFileText = () => {
    if ( isEditing ) return null;
    return <FileItemText fileItem={fileItem} />;
  }

  const renderFileNameEdit = () => {
    if ( !isEditing ) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Input
          sx={{ maxWidth: 400 }}
          value={fileNameValue}
          onChange={(e) => setFileNameValue(e.target.value)}
          size="small"
          margin="none"
        />
        <Tooltip title="Cancel">
          <IconButton
            name={`Cancel edit file name ${fileItem.id}`}
            onClick={handleCancelRenameClick}
            color="error"
          ><ClearIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Save">
          <IconButton
            name={`Save file name ${fileItem.id}`}
            onClick={handleSaveRenameClick}
          ><CheckIcon /></IconButton>
        </Tooltip>
      </Box>
    )
  }

  const showPreviewMenuItem = fileItem.file.type && fileItem.file.type.includes('pdf');
  const showRemoveButton = typeof group.errorCode !== 'undefined';
  const showRenameButton = fileItem.errors
    .some((error:IValidateError) => 
      error.errorCode === ValidateErrorCodes.DuplicateName ||
      error.errorCode === ValidateErrorCodes.NameExists
    );
  const showKeepThisButton = fileItem.errors.some((error:IValidateError) => error.errorCode === ValidateErrorCodes.DuplicateContent)

  return (
    <Fragment>
      <Box sx={{ alignSelf: 'center', flex: 1, overflow: 'hidden' }}>
        {renderFileNameEdit()}
        {renderFileText()}
        {fileItem.errors.length ? (
          <FormHelperText
            sx={{ mt: 0 }}
            dangerouslySetInnerHTML={{ __html: fileItem.errors.map((error) => error.errorMessage).join(' | ') }}
            error
          />
        ) : null}
      </Box>
      {showKeepThisButton ? (
        <Button
          sx={{ mr: 2 }}
          name={`Keep this file ${fileItem.id}`}
          onClick={handleGroupRemoveClick}
          variant="outlined"
        >Keep this file only</Button>
      ) : null}
      {showRenameButton ? (
        <Button
          sx={{ mr: 2 }}
          name={`Rename file name ${fileItem.id}`}
          onClick={handleRenameClick}
          variant="outlined"
        >Rename</Button>
      ) : null}
      {showRemoveButton ? (
        <Button
          sx={{ mr: 2 }}
          name={`Remove file ${fileItem.id}`}
          onClick={handleRemoveClick}
          variant="outlined"
          color="error"
        >Remove</Button>
      ) : null}
      {allowFileCollection ? (
        <FileItemCollectionSelector fileItem={fileItem} />
      ) : null}
      <IconButton
        name={`File context menu ${fileItem.id}`}
        aria-label="File context menu"
        onClick={openMenu}
      ><MoreVertIcon /></IconButton>
      <Menu>
        {showPreviewMenuItem ? (
          <MenuItem
            name={`Preview file ${fileItem.id}`}
            onClick={handlePreviewClick}
          >{t('buttons.preview')}</MenuItem>
        ) : null}
        {allowFileComment ? (
          <MenuItem
            name={`Comment file ${fileItem.id}`}
            onClick={toggle}
          >{t('buttons.comment')}</MenuItem>
        ) : null}
        <MenuItem
          name={`Remove file ${fileItem.id}`}
          sx={{
            color: (theme:Theme) => theme.palette.error.main
          }}
          onClick={handleRemoveClick}
        >{t('buttons.delete')}</MenuItem>
      </Menu>
      {open ? (
        <FileItemCommentDialog
          open={open}
          onClose={handleClose}
          comment={fileItem.comment}
        />
      ) : null}
    </Fragment>
  )
}

export default FileItem;
